"use client";

/**
 * HomeHero — the new landing hero (Rivky's redesign, ref: shaniwigs.com).
 * A full-bleed photo of the models with two CTAs: "Book an Appointment"
 * (opens the "What brings you in?" picker) and "Shop Wigs" (→ /wigs).
 * The page below it shows featured wigs, then client testimonials.
 */

import { useState, useEffect } from "react";
import Image from "next/image";
import { Link } from "@/i18n/routing";
import { useTranslations } from "next-intl";
import {
  X,
  Calendar,
  Scissors,
  Sparkles,
  Palette,
  Droplets,
  ShoppingBag,
  ArrowRight,
} from "lucide-react";

const OPTIONS = [
  { href: "/wigs", icon: ShoppingBag, key: "buyWig" },
  { href: "/book?service=new_wig", icon: Calendar, key: "consult" },
  { href: "/book?service=wash_set", icon: Droplets, key: "washSet" },
  { href: "/book?service=repair", icon: Scissors, key: "repair" },
  { href: "/book?service=color", icon: Palette, key: "color" },
  { href: "/book?service=restyle", icon: Sparkles, key: "restyle" },
] as const;

export default function HomeHero() {
  const t = useTranslations("homeHero");
  const [open, setOpen] = useState(false);

  // Lock body scroll while the picker modal is open.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  return (
    <>
      <section className="relative flex min-h-[82vh] items-center overflow-hidden bg-brand-dark">
        <Image
          src="/images/home/hero-models.jpg"
          alt=""
          fill
          priority
          sizes="100vw"
          className="object-cover object-[center_18%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/45 to-brand-dark/25" />

        <div className="relative mx-auto w-full max-w-4xl px-4 py-24 text-center text-white sm:px-6">
          <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.35em] text-brand-gold">
            {t("eyebrow")}
          </p>
          <h1 className="font-display text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">
            {t("headline")}
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-white/85 sm:text-base">
            {t("subtitle")}
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="w-full rounded-full bg-brand-gold px-8 py-3.5 text-sm font-medium uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-brand-dark sm:w-auto"
            >
              {t("bookCta")}
            </button>
            <Link
              href="/wigs"
              className="w-full rounded-full border border-white/50 px-8 py-3.5 text-center text-sm font-medium uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-brand-dark sm:w-auto"
            >
              {t("shopCta")}
            </Link>
          </div>
        </div>
      </section>

      {/* "What brings you in?" picker */}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-brand-dark/70 p-4 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t("modalTitle")}
        >
          <div
            className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t("close")}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-brand-muted transition-colors hover:bg-brand-cream hover:text-brand-dark"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="mb-6 text-center">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.3em] text-brand-gold">
                {t("modalEyebrow")}
              </p>
              <h2 className="font-display text-2xl text-brand-dark sm:text-3xl">
                {t("modalTitle")}
              </h2>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {OPTIONS.map((o) => {
                const Icon = o.icon;
                return (
                  <Link
                    key={o.key}
                    href={o.href}
                    onClick={() => setOpen(false)}
                    className="group flex flex-col items-center gap-2.5 rounded-xl border border-gray-100 bg-white p-4 text-center transition-all hover:border-brand-gold/40 hover:shadow-md sm:p-5"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-cream text-brand-gold transition-colors group-hover:bg-brand-gold group-hover:text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-sm font-medium text-brand-dark">
                      {t(`opt_${o.key}`)}
                    </span>
                  </Link>
                );
              })}
            </div>

            <div className="mt-5 text-center">
              <Link
                href="/book"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-brand-gold hover:text-brand-gold-dark"
              >
                {t("seeAll")}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
